import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Configuration options for the trip activation hook
 */
export interface UseTripActivationOptions {
	/** ISO date string representing when the trip becomes active */
	tripActivationTime: string;
	/** Polling interval in milliseconds (default: 30000 = 30 seconds) */
	pollingIntervalMs?: number;
}

/**
 * Determines if a trip is currently active based on activation time
 * Similar to isAfterThirtyMinutes but checks if current time >= activation time
 *
 * @param isoDateString - ISO date string representing when the trip becomes active
 * @returns true if current time >= trip activation time
 */
const checkTripActive = (isoDateString: string): boolean => {
	const currentDate = new Date();
	const tripActivationDate = new Date(isoDateString);
	// Both dates store UTC milliseconds internally, so comparison works across all timezones
	return currentDate.getTime() >= tripActivationDate.getTime();
};

/**
 * Gets the time remaining until trip activation
 *
 * @param isoDateString - ISO date string representing when the trip becomes active
 * @returns milliseconds until trip activation (negative if already active)
 */
const getTimeUntilActivation = (isoDateString: string): number => {
	const currentDate = new Date();
	const tripActivationDate = new Date(isoDateString);
	// getTime() returns UTC milliseconds, so difference is timezone-independent
	return tripActivationDate.getTime() - currentDate.getTime();
};

/**
 * Hook that polls trip activation status at regular intervals
 *
 * @param options - Configuration options for polling and trip activation
 * @returns Object containing current trip status and polling state
 */
export const useTripActivation = (options: UseTripActivationOptions) => {
	const {
		tripActivationTime,
		pollingIntervalMs = 30000, // Default: 30 seconds
	} = options;

	const [isTripActive, setIsTripActive] = useState(false);
	const [timeUntilActivation, setTimeUntilActivation] = useState(0);
	const [isPolling, setIsPolling] = useState(false);

	// Use ref to store interval ID for cleanup
	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	// Function to check and update trip status (memoized to avoid unnecessary re-renders)
	const updateTripStatus = useCallback(() => {
		const isActive = checkTripActive(tripActivationTime);
		const timeRemaining = getTimeUntilActivation(tripActivationTime);
		setIsTripActive(isActive);
		setTimeUntilActivation(timeRemaining);
	}, [tripActivationTime]);

	// Function to manually stop polling
	const stopPolling = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
		setIsPolling(false);
	}, []);

	useEffect(() => {
		// Initial check
		updateTripStatus();

		// Start polling if trip is not yet active
		if (!isTripActive) {
			setIsPolling(true);

			intervalRef.current = setInterval(() => {
				updateTripStatus();

				// Stop polling once trip becomes active
				const currentStatus = checkTripActive(tripActivationTime);
				if (currentStatus) {
					stopPolling();
				}
			}, pollingIntervalMs);
		} else {
			setIsPolling(false);
		}

		// Cleanup function
		return () => {
			stopPolling();
		};
	}, [
		tripActivationTime,
		pollingIntervalMs,
		isTripActive,
		updateTripStatus,
		stopPolling,
	]);

	return {
		isTripActive,
		timeUntilActivation,
		isPolling,
	};
};
